import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useForm, Controller } from "react-hook-form";
import { useReportsStore } from "../../../store/reports/reportsStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../context/AlertContext";
import InfiniteDeviceSelect, { useReportDeviceIds } from "../../../components/InfiniteDeviceSelect";
import { allDevicesQueryOptions } from "../../../features/devices/queries";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import { useReportStopsStore, type StopBucket } from "../../../store/reports/reportStopsStore";
import ReportPointsTable, { type ReportTableColumn } from "../components/ReportPointsTable";
import { ReportStatCard, ReportChart, tickColor, gridColor } from "../components/ReportCharts";
import ReportLoadingPanel from "../components/ReportLoadingPanel";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import type { ChartConfiguration } from "chart.js";
import ReportActionButton from "../components/ReportActionButton";
import ReportDateRangePicker from "../components/ReportDateRangePicker";
import ReportFormatToggle from "../components/ReportFormatToggle";
import { toLocalInputValue } from "../utils/reportDateFormat";

const MAX_RANGE_DAYS = 31;

const BUCKET_LABELS: Record<StopBucket, string> = {
  "0_5": "0 a 5 min",
  "5_15": "5 a 15 min",
  "15_30": "15 a 30 min",
  "30_60": "30 a 60 min",
  "60_mas": "+60 min",
};
const BUCKET_ORDER: StopBucket[] = ["0_5", "5_15", "15_30", "30_60", "60_mas"];

interface StopFormData {
  devices: string[];
  startDate: string;
  endDate: string;
  typeReport: string;
  typeDocument: "excel" | "pdf";
}

interface StopRow {
  id: string;
  unidad: string;
  totalParadas: string;
  paradas_0_5: string;
  paradas_5_15: string;
  paradas_15_30: string;
  paradas_30_60: string;
  paradas_60_mas: string;
  tiempoTotal: string;
}

const STOP_COLUMNS: ReportTableColumn<StopRow>[] = [
  { key: "unidad", header: "Unidad" },
  { key: "totalParadas", header: "Total", align: "right" },
  { key: "paradas_0_5", header: "0-5 min", align: "right" },
  { key: "paradas_5_15", header: "5-15 min", align: "right" },
  { key: "paradas_15_30", header: "15-30 min", align: "right" },
  { key: "paradas_30_60", header: "30-60 min", align: "right" },
  { key: "paradas_60_mas", header: "+60 min", align: "right" },
  { key: "tiempoTotal", header: "Tiempo total" },
];

const fmtDuration = (seconds: number): string => {
  if (!seconds || seconds < 0) return "00:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export default function StopSection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const generateHistoryReport = useReportsStore((state) => state.generateHistoryReport);
  const loading = useReportsStore((state) => state.loading);
  const { loadedDeviceIds, loadedCount, totalDevices } = useReportDeviceIds();

  const loadRange = useReportStopsStore((state) => state.loadRange);
  const clearStops = useReportStopsStore((state) => state.clear);
  const resultsState = useReportStopsStore((state) => state.results);
  const order = useReportStopsStore((state) => state.order);
  const stopsLoading = useReportStopsStore((state) => state.loading);

  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [loadAttempted, setLoadAttempted] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<StopFormData>({
    mode: "onBlur",
    defaultValues: {
      devices: [],
      startDate: "",
      endDate: "",
      typeReport: "stops",
      typeDocument: "pdf",
    },
  });

  const { showAlert } = useAlert();
  const { companyId, disableWithoutCompany } = useReportCompanyScope(setValue, () => {
    clearStops();
    setLoadAttempted(false);
  });
  const { data: allDevices } = useQuery({ ...allDevicesQueryOptions(companyId) });

  const selectedDevices = watch("devices");
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const hasDevices = (selectedDevices?.length ?? 0) > 0;
  const hasDates = Boolean(startDate && endDate);
  const rangeDays = useMemo(() => {
    if (!hasDates) return 0;
    return (new Date(endDate).getTime() - new Date(startDate).getTime()) / (24 * 60 * 60 * 1000);
  }, [endDate, hasDates, startDate]);
  const hasValidRange = hasDates && rangeDays >= 0;
  const isWithinViewLimit = hasValidRange && rangeDays <= MAX_RANGE_DAYS;
  const filtersReadyForView = hasDevices && hasValidRange && isWithinViewLimit;

  const handleSelectAll = () => {
    if (selectedDevices?.length === loadedDeviceIds.length) {
      setValue("devices", []);
    } else {
      setValue("devices", loadedDeviceIds);
    }
  };

  const onSubmit = async (formData: StopFormData) => {
    const result = await generateHistoryReport(
      {
        deviceIds: formData.devices,
        startDate: formData.startDate,
        endDate: formData.endDate,
        typeReport: formData.typeReport,
        typeDocument: formData.typeDocument,
      },
      showAlert,
      () => {},
    );

    if (result.success && result.data) {
      const extension = formData.typeDocument === "pdf" ? "pdf" : "xlsx";
      const blob = new Blob([result.data], {
        type:
          formData.typeDocument === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.fileName || `reporte.${extension}`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const resolveNameById = useCallback((): Record<string, string> => {
    const map: Record<string, string> = {};
    (allDevices ?? []).forEach((d: any) => {
      map[d.id] = d.vehicle?.name || d.identifier || d.id;
    });
    return map;
  }, [allDevices]);

  const onViewStops = async () => {
    const { devices, startDate, endDate } = getValues();

    if (!devices || devices.length === 0) {
      showAlert({
        title: "Selecciona unidades",
        text: "Selecciona al menos un equipo para ver las paradas.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    if (!startDate || !endDate) {
      showAlert({
        title: "Fechas requeridas",
        text: "Selecciona la fecha de inicio y fin.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const days = (new Date(endDate).getTime() - new Date(startDate).getTime()) / (24 * 60 * 60 * 1000);
    if (days > MAX_RANGE_DAYS) {
      showAlert({
        title: "Rango demasiado amplio",
        text: `Ver paradas solo muestra hasta ${MAX_RANGE_DAYS} días. Para rangos mayores usa "Generar Reporte".`,
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const fromIso = new Date(startDate).toISOString();
    const toIso = new Date(endDate).toISOString();
    const nameById = resolveNameById();
    const devicesToLoad = devices.map((id) => ({
      deviceId: id,
      vehicleName: nameById[id] || id,
    }));

    await loadRange(devicesToLoad, fromIso, toIso);
    setLoadAttempted(true);
  };

  const orderedResults = useMemo(
    () => order.map((id) => resultsState[id]).filter(Boolean),
    [order, resultsState],
  );

  const totals = useMemo(() => {
    const buckets: Record<StopBucket, number> = {
      "0_5": 0,
      "5_15": 0,
      "15_30": 0,
      "30_60": 0,
      "60_mas": 0,
    };
    let totalParadas = 0;
    let tiempoTotal = 0;
    let withData = 0;
    orderedResults.forEach((r) => {
      totalParadas += r.totalParadas;
      tiempoTotal += r.tiempoTotalSeconds;
      if (r.totalParadas > 0) withData++;
      BUCKET_ORDER.forEach((b) => (buckets[b] += r.buckets[b]));
    });
    return { buckets, totalParadas, tiempoTotal, withData };
  }, [orderedResults]);

  const tableRows = useMemo<StopRow[]>(
    () =>
      orderedResults.map((r) => ({
        id: r.deviceId,
        unidad: r.vehicleName,
        totalParadas: String(r.totalParadas),
        paradas_0_5: String(r.buckets["0_5"]),
        paradas_5_15: String(r.buckets["5_15"]),
        paradas_15_30: String(r.buckets["15_30"]),
        paradas_30_60: String(r.buckets["30_60"]),
        paradas_60_mas: String(r.buckets["60_mas"]),
        tiempoTotal: fmtDuration(r.tiempoTotalSeconds),
      })),
    [orderedResults],
  );

  const chartConfig = useMemo<ChartConfiguration | null>(() => {
    if (totals.totalParadas === 0) return null;
    return {
      type: "bar",
      data: {
        labels: BUCKET_ORDER.map((b) => BUCKET_LABELS[b]),
        datasets: [
          {
            label: "Paradas",
            data: BUCKET_ORDER.map((b) => totals.buckets[b]),
            backgroundColor: ["#16A34A", "#84CC16", "#D97706", "#F97316", "#DC2626"],
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { color: tickColor(isDarkMode) }, grid: { display: false } },
          y: {
            beginAtZero: true,
            ticks: { color: tickColor(isDarkMode), precision: 0 },
            grid: { color: gridColor(isDarkMode) },
          },
        },
      },
    };
  }, [totals, isDarkMode]);

  const onDownload = async (typeDocument: "pdf" | "excel") => {
    if (tableRows.length === 0) return;
    const { startDate, endDate } = getValues();
    const extension = typeDocument === "pdf" ? "pdf" : "xlsx";
    const fileName = `stops_${startDate.split("T")[0]}_${endDate.split("T")[0]}.${extension}`;
    const period =
      `Período: ${startDate.replace("T", " ")} al ${endDate.replace("T", " ")}` +
      ` | Total paradas: ${totals.totalParadas}`;

    setExporting(typeDocument);
    await new Promise((r) => setTimeout(r, 50));
    try {
      const meta = { title: "Reporte de Paradas", period, fileName };
      if (typeDocument === "excel") {
        await exportHistoricToExcel(tableRows, STOP_COLUMNS, meta);
      } else {
        exportHistoricToPdf(tableRows, STOP_COLUMNS, meta);
      }
    } catch {
      showAlert({
        title: "Error al descargar",
        text: "No se pudo generar el archivo de paradas.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    setValue("startDate", toLocalInputValue(new Date()));
    setValue("endDate", toLocalInputValue(new Date(new Date().setDate(new Date().getDate() + 15))));
  }, []);

  useEffect(() => {
    return () => {
      clearStops();
      setLoadAttempted(false);
    };
  }, [clearStops]);

  const hasResults = orderedResults.length > 0;
  const showSkeleton = stopsLoading;
  const showEmptyState = !hasResults && !showSkeleton;

  const summaryCards = [
    { label: "Total de paradas", value: String(totals.totalParadas), icon: "ri-parking-box-line" },
    { label: "Tiempo detenido", value: fmtDuration(totals.tiempoTotal), icon: "ri-time-line" },
    { label: "Paradas +60 min", value: String(totals.buckets["60_mas"]), icon: "ri-alarm-warning-line" },
    { label: "Unidades con paradas", value: String(totals.withData), icon: "ri-truck-line" },
  ];

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4">
      <form onSubmit={handleSubmit(onSubmit)} className={`flex flex-col lg:flex-row ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-3.5 gap-3 justify-between lg:items-end`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex flex-col gap-1 min-w-52 w-full lg:w-auto">
            <div className="flex justify-between items-center">
              <label htmlFor="devices" className={`${themeClasses.text} text-xs`}>
                Equipos {selectedDevices?.length > 0 && `(${selectedDevices.length}/${totalDevices})`}
              </label>
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={disableWithoutCompany}
                className={`text-xs px-2 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} hover:opacity-90 disabled:opacity-50`}
              >
                {selectedDevices?.length === loadedDeviceIds.length && loadedCount > 0 ? "Deseleccionar todos" : `Seleccionar todos (${loadedCount})`}
              </button>
            </div>
            <div>
              <Controller
                name="devices"
                control={control}
                rules={{ required: "Selecciona al menos un equipo" }}
                render={({ field }) => (
                  <InfiniteDeviceSelect
                    value={field.value}
                    onChange={field.onChange}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                  />
                )}
              />
              {errors.devices && <span className="text-red-500 text-sm">{errors.devices.message}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Rango de fechas</label>
            <input type="hidden" {...register("startDate", { required: "Fecha inicio requerida" })} />
            <input type="hidden" {...register("endDate", { required: "Fecha fin requerida" })} />
            <ReportDateRangePicker
              startValue={watch("startDate")}
              endValue={watch("endDate")}
              onChangeStart={(v) => setValue("startDate", v, { shouldValidate: true, shouldDirty: true })}
              onChangeEnd={(v) => setValue("endDate", v, { shouldValidate: true, shouldDirty: true })}
              startError={errors.startDate?.message}
              endError={errors.endDate?.message}
            />
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Formato</label>
            <Controller
              name="typeDocument"
              control={control}
              render={({ field }) => <ReportFormatToggle value={field.value} onChange={field.onChange} />}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <ReportActionButton
            type="button"
            variant="secondary"
            onClick={onViewStops}
            disabled={stopsLoading || !filtersReadyForView || disableWithoutCompany}
            title={!filtersReadyForView && hasDevices && hasValidRange && !isWithinViewLimit ? `Ver paradas solo admite hasta ${MAX_RANGE_DAYS} días` : undefined}
          >
            Ver paradas
          </ReportActionButton>
          <ReportActionButton
            type="submit"
            disabled={loading || !hasDevices || !hasValidRange || disableWithoutCompany}
          >
            {loading ? "Generando..." : "Generar Reporte"}
          </ReportActionButton>
        </div>
      </form>

      {showSkeleton && (
        <ReportLoadingPanel
          isDarkMode={isDarkMode}
          icon="ri-parking-box-line"
          title="Calculando paradas…"
          subtitle="Obteniendo posiciones del rango seleccionado"
        />
      )}

      {hasResults && !showSkeleton && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className={`text-sm ${themeClasses.text}`}>
              {totals.withData} de {orderedResults.length} unidad(es) con paradas
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onDownload("pdf")}
                disabled={exporting !== null}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
              >
                {exporting === "pdf" ? "Generando..." : "Descargar PDF"}
              </button>
              <button
                type="button"
                onClick={() => onDownload("excel")}
                disabled={exporting !== null}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
              >
                {exporting === "excel" ? "Generando..." : "Descargar Excel"}
              </button>
              <button
                type="button"
                onClick={() => {
                  clearStops();
                  setLoadAttempted(false);
                }}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2}`}
              >
                Cerrar
              </button>
            </div>
          </div>

          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            {summaryCards.map((card) => (
              <ReportStatCard key={card.label} icon={card.icon} label={card.label} value={card.value} isDarkMode={isDarkMode} />
            ))}
          </div>

          {chartConfig && (
            <div className={`sticky top-0 z-20 p-3 rounded-xl border shadow-sm ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-50 border-gray-200"}`}>
              <p className={`text-sm font-semibold mb-2 ${themeClasses.text}`}>Paradas por duración</p>
              <ReportChart config={chartConfig} height={280} />
            </div>
          )}

          <ReportPointsTable
            data={tableRows}
            columns={STOP_COLUMNS}
            isDarkMode={isDarkMode}
            emptyText="No se encontraron paradas en el rango seleccionado."
          />
        </div>
      )}

      {showEmptyState && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className={`relative w-full overflow-hidden rounded-xl border border-dashed ${themeClasses.cardBorder} ${isDarkMode ? "bg-white/5" : "bg-black/[0.02]"}`} style={{ minHeight: 220 }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <span className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${isDarkMode ? "bg-primary-500/15" : "bg-primary-500/10"}`}>
                <i className="ri-parking-box-line text-primary-500 text-2xl" />
              </span>
              <div>
                <p className={`text-sm font-semibold ${themeClasses.text}`}>Paradas por unidad</p>
                <p className={`text-xs mt-1 max-w-md ${themeClasses.textSecondary}`}>
                  Configura equipos y fechas, y usa <strong>Ver paradas</strong> (hasta {MAX_RANGE_DAYS} días) para ver el resumen por rango de duración. Para rangos mayores usa <strong>Generar Reporte</strong>.
                </p>
              </div>
            </div>
          </div>

          {loadAttempted && !stopsLoading && (
            <div className={`p-3 rounded-xl border ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-100 border-gray-200"}`}>
              <div className="flex items-start space-x-2">
                <i className={`ri-information-line text-base ${themeClasses.textSecondary}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${themeClasses.textSecondary}`}>Sin resultados</p>
                  <p className={`text-sm ${themeClasses.text} mt-0.5`}>
                    No se encontraron paradas en el rango seleccionado. Verifica equipos y fechas, o usa <strong>Generar Reporte</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
